import { formatMoney, type CurrencyCode } from '@harbor/shared';

/**
 * Display formatting.
 *
 * The money formatter lives in `@harbor/shared` and is imported rather than
 * reimplemented — the storefront and the API must never disagree about what a
 * total looks like, and the only way to guarantee that is to have one function.
 */

export const money = formatMoney;

export function percentFromBps(bps: number): string {
  const value = bps / 100;
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';

  const seconds = Math.round((Date.now() - then) / 1000);
  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  const thresholds: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.35, 'week'],
    [12, 'month'],
  ];

  let value = seconds;
  for (const [limit, unit] of thresholds) {
    if (Math.abs(value) < limit) return formatter.format(-Math.round(value), unit);
    value /= limit;
  }
  return formatter.format(-Math.round(value), 'year');
}

export function shortDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

export function dateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** `24 in stock`, `3 left`, `Sold out`. */
export function stockLabel(stock: number): { text: string; tone: 'positive' | 'warning' | 'danger' } {
  if (stock <= 0) return { text: 'Sold out', tone: 'danger' };
  if (stock <= 5) return { text: `${stock} left`, tone: 'warning' };
  return { text: `${stock} in stock`, tone: 'positive' };
}

export function orderStatusTone(
  status: string,
): 'positive' | 'warning' | 'danger' | 'neutral' {
  switch (status) {
    case 'paid':
      return 'positive';
    case 'fulfilled':
      return 'positive';
    case 'pending_payment':
      return 'warning';
    case 'cancelled':
      return 'danger';
    case 'refunded':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function titleCase(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function currencyOf(value: string): CurrencyCode {
  return (value as CurrencyCode) ?? 'USD';
}

/** `HB-2026-000042` → `#000042`, for dense tables. */
export function shortOrderNumber(orderNumber: string): string {
  const parts = orderNumber.split('-');
  return `#${parts.at(-1) ?? orderNumber}`;
}
